import type { RenderContext, VideoFrame } from '../../types';
import { WebGLShaderProgram } from '../../gpu/shader';
import { BaseEffect } from '../base';
import { VERTEX_FULLSCREEN, FRAG_CHROMATIC_ABERRATION } from '../../shaders/glsl';

const DEFAULTS = { amount: 0.55, radial: 0.85 };

let caProg: WebGLShaderProgram | null = null;

export class ChromaticAberrationEffect extends BaseEffect {
  constructor(id: string, clip: Partial<import('../../types').EffectTimelineClip>) {
    super(id, 'chromatic_aberration', clip, DEFAULTS);
  }

  protected renderGpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const gl = context.gpu!.gl;
    if (!caProg) caProg = new WebGLShaderProgram(gl, VERTEX_FULLSCREEN, FRAG_CHROMATIC_ABERRATION);
    return this.runShaderPass(frame, caProg, (_gl, prog) => {
      prog.setUniform1i('u_texture', 0);
      prog.setUniform1f('u_amount', params.amount);
      prog.setUniform1f('u_radial', params.radial);
    }, context);
  }

  protected renderCpu(frame: VideoFrame, params: Record<string, number>, _context: RenderContext): VideoFrame {
    const canvas = document.createElement('canvas');
    canvas.width = frame.width;
    canvas.height = frame.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return frame;
    const src = frame.canvas as CanvasImageSource;
    const shift = Math.max(1, Math.round(params.amount * (1 + params.radial) * 3));
    ctx.drawImage(src, 0, 0);
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = 0.45;
    ctx.drawImage(src, -shift, 0);
    ctx.globalAlpha = 0.35;
    ctx.drawImage(src, shift, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    return { width: frame.width, height: frame.height, canvas, texture: null };
  }
}
